import React, { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { Check, BookOpen } from 'lucide-react';
import { getNodeTypeColor, NodeType } from '../workspace/types';
import type { TutorialStep } from './ResearchWelcomePanel';

const TOTAL_STEPS = 4;
const STEP_DELAY_MS = 1100;
const INITIAL_DELAY_MS = 600;


function useStaggeredReveal() {
  const reducedMotion = usePrefersReducedMotion();
  const [revealedCount, setRevealedCount] = useState(
    reducedMotion ? TOTAL_STEPS + 1 : 0,
  );

  useEffect(() => {
    if (reducedMotion) return;
    if (revealedCount > TOTAL_STEPS) return;
    const delay = revealedCount === 0 ? INITIAL_DELAY_MS : STEP_DELAY_MS;
    const t = setTimeout(() => setRevealedCount((n) => n + 1), delay);
    return () => clearTimeout(t);
  }, [revealedCount, reducedMotion]);

  const skipAnimation = () => setRevealedCount(TOTAL_STEPS + 1);
  return { revealedCount, skipAnimation, reducedMotion };
}

/** Same single-source contract as RESEARCH_TUTORIAL_STEPS — consumed by this
 *  panel's grid AND the start page's carousel (BAR-538). */
export const BUILD_TUTORIAL_STEPS: TutorialStep[] = [
  { title: 'Describe what you want to build', mock: <ChatComposerPreview /> },
  { title: 'BARK plans the steps to reach the goal', mock: <PlanTreePreview /> },
  { title: 'BARK works through the plan, step by step', mock: <WorkingPreview /> },
  { title: 'You get a finished deliverable', mock: <DeliverablesPreview /> },
];

export default function BuildWelcomePanel() {
  const { revealedCount, skipAnimation, reducedMotion } = useStaggeredReveal();
  const showAll = revealedCount > TOTAL_STEPS;

  return (
    <div className="w-full h-full bg-bg-warm overflow-auto">
      {/* pt-20 (vs. py-10) keeps the heading at the same height as
          ResearchWelcomePanel so the two welcome screens line up when
          switching between Build and Research. Build has no Report/Graph
          tab pill above it, but matching the spacing is what keeps the
          two titles in the same row. */}
      <div className="mx-auto max-w-[1180px] px-8 pt-20 pb-10">
        <header className="text-center mb-10">
          <h1 className="font-heading text-[40px] leading-[1.15] text-fg tracking-[-0.02em]">
            Welcome to Build
          </h1>
          <p className="mt-2 text-[14px] text-fg-secondary">
            Describe your goal. BARK plans, researches, and builds.
          </p>
          {!reducedMotion && (
            <button
              type="button"
              onClick={skipAnimation}
              aria-hidden={showAll}
              tabIndex={showAll ? -1 : 0}
              className={`mt-3 text-[11px] text-fg-tertiary hover:text-fg-secondary
                         underline decoration-dotted underline-offset-2
                         transition-opacity duration-300
                         ${showAll ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              Skip intro
            </button>
          )}
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BUILD_TUTORIAL_STEPS.map((step, i) => (
            <Column
              key={step.title}
              index={i}
              revealedCount={revealedCount}
              number={i + 1}
              title={step.title}
              mock={step.mock}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Column({
  index,
  revealedCount,
  number,
  title,
  mock,
}: {
  index: number;
  revealedCount: number;
  number: number;
  title: string;
  mock: React.ReactNode;
}) {
  const isVisible = revealedCount > index;
  const isCurrent = revealedCount === index + 1;

  return (
    <div
      className={`flex flex-col transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md
                     font-mono text-[13px] font-semibold transition-colors duration-300
                     ${
                       isCurrent
                         ? 'bg-accent-copper text-white'
                         : 'bg-accent-copper-tint text-accent-copper'
                     }`}
          aria-hidden="true"
        >
          {number}
        </span>
        <h2 className="text-[14px] font-semibold leading-[1.35] text-fg pt-0.5">
          {title}
        </h2>
      </div>
      <div className="flex-1">{mock}</div>
    </div>
  );
}

// =============================================================================
// COLUMN 1 — chat composer preview
// Mirrors the real composer in GeminiChatArea.tsx (textarea card with
// rounded-xl border, bottom row holding WorkspaceIndicator-style label,
// ContextWindowBar-style usage strip, and the copper paper-plane send button).
// Static — non-interactive, no state, no submit handler.
// =============================================================================

function ChatComposerPreview() {
  return (
    <div
      className="bg-surface border border-border-default rounded-xl shadow-sm"
      aria-hidden="true"
    >
      {/* Textarea slot — rendered as a static div so no caret blinks in a tutorial */}
      <div className="px-3 pt-2.5 pb-1 text-[13px] text-fg leading-snug min-h-[34px]">
        Help me build an NMD model
      </div>

      {/* Bottom row — workspace indicator + context bar + send */}
      <div className="flex items-center justify-between px-3 pb-2 pt-1 border-t border-divider gap-2">
        {/* Center: indicator + context strip */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1 text-[10px] text-fg-tertiary shrink-0">
            <img
              src="/Data/images/BARK_tree_2_navy.png"
              alt=""
              className="w-3 h-3 object-contain"
            />
            <span className="font-medium">Plan</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] min-w-0">
            <span className="text-fg-tertiary">Context:</span>
            <div className="h-[3px] w-8 bg-bg-warm rounded-full overflow-hidden">
              <div className="h-full bg-success" style={{ width: '0%' }} />
            </div>
            <span className="font-medium text-success whitespace-nowrap">0k/200k</span>
          </div>
        </div>

        {/* Right: copper send button — paper-plane SVG matches the real composer */}
        <span
          className="inline-flex items-center justify-center p-1 rounded bg-accent-copper text-white shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22,2 15,22 11,13 2,9" />
          </svg>
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// COLUMN 2 — plan tree
// Each card mirrors GraphNode's chrome (3px left accent stripe, uppercase type
// label in node-type color, bold body label, mono deliverable line). Colors
// come from getNodeTypeColor() in workspace/types so they track production.
// =============================================================================

type WorkspaceKind = 'institution_context' | 'research' | 'production';

const WORKSPACE_TO_NODE_TYPE: Record<WorkspaceKind, NodeType> = {
  institution_context: 'task',
  research: 'task',
  production: 'component',
};

const WORKSPACE_TO_LABEL: Record<WorkspaceKind, string> = {
  institution_context: 'Context',
  research: 'Research',
  production: 'Build',
};

function PlanNodePreview({
  workspace,
  label,
  deliverable,
  active,
}: {
  workspace: WorkspaceKind;
  label: string;
  deliverable?: string;
  active?: boolean;
}) {
  const nodeType = WORKSPACE_TO_NODE_TYPE[workspace];
  const color = getNodeTypeColor(nodeType);
  const typeLabel = WORKSPACE_TO_LABEL[workspace];

  return (
    <div
      className="relative w-full rounded-lg border bg-surface px-2.5 py-2 shadow-bark-xs overflow-hidden"
      style={{ borderColor: active ? color : 'var(--border-default)' }}
    >
      {/* 3px left accent bar */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: color }}
      />

      {/* Type label */}
      <div
        className="text-[9px] font-bold uppercase tracking-[0.08em] leading-none mb-1"
        style={{ color }}
      >
        {typeLabel}
      </div>

      {/* Body label */}
      <div className="text-[11px] leading-tight font-medium text-fg">{label}</div>

      {/* Deliverable meta */}
      {deliverable && (
        <div className="mt-1 font-mono text-[9px] text-fg-secondary tabular-nums truncate">
          {deliverable}
        </div>
      )}
    </div>
  );
}

function PlanTreePreview() {
  return (
    <div
      className="rounded-lg border border-border-default bg-surface p-3 shadow-bark-xs"
      aria-hidden="true"
    >
      <div className="flex flex-col items-stretch gap-2">
        {/* Top */}
        <div className="px-3">
          <PlanNodePreview
            workspace="institution_context"
            label="Institution profile"
            deliverable="Institution context"
          />
        </div>

        <Connector variant="split" />

        {/* Middle row — Build (production) | Research */}
        <div className="grid grid-cols-2 gap-2">
          <PlanNodePreview
            workspace="production"
            label="Data preparation"
            deliverable="Validated dataset"
          />
          <PlanNodePreview
            workspace="research"
            label="NMD requirements"
            deliverable="Model spec"
          />
        </div>

        <Connector variant="merge" />

        {/* Bottom */}
        <div className="px-3">
          <PlanNodePreview
            workspace="production"
            label="NMD model"
            deliverable="NMD model"
          />
        </div>
      </div>
    </div>
  );
}

function Connector({ variant }: { variant: 'split' | 'merge' }) {
  const isSplit = variant === 'split';
  return (
    <svg
      viewBox="0 0 100 16"
      preserveAspectRatio="none"
      className="w-full h-3 text-[color:var(--border-strong)]"
      aria-hidden="true"
    >
      {isSplit ? (
        <>
          <path d="M50 0 V6" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M22 16 V10 H78 V16" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M50 6 V10" stroke="currentColor" strokeWidth="1" fill="none" />
        </>
      ) : (
        <>
          <path d="M22 0 V6 H78 V0" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M50 6 V16" stroke="currentColor" strokeWidth="1" fill="none" />
        </>
      )}
    </svg>
  );
}

// =============================================================================
// COLUMN 3 — active research card + completed step list
// Top: PlanNodePreview in active state with a pulsing dot + "BARK is working…"
// Bottom: list of completed steps with green checkmarks (matches the green
// success-bg + check style used elsewhere in the workspace surface).
// =============================================================================

function WorkingPreview() {
  const steps = [
    'Query graph database',
    'Analyze requirements',
    'Populate graph entities',
    'Validate and check coverage',
    'Update model spec',
  ];
  return (
    <div
      className="rounded-lg border border-border-default bg-surface p-3 shadow-bark-xs"
      aria-hidden="true"
    >
      <PlanNodePreview
        workspace="research"
        label="NMD requirements"
        active
      />

      <div className="flex items-center gap-1.5 mt-2 mb-3 px-1">
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-success opacity-60 animate-ping" />
          <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-success" />
        </span>
        <span className="text-[10px] text-fg-secondary">BARK is working…</span>
      </div>

      <ul className="flex flex-col gap-1.5">
        {steps.map((s) => (
          <li key={s} className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-fg-secondary leading-tight">{s}</span>
            <span className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-success-bg text-success">
              <Check className="w-2.5 h-2.5" strokeWidth={3} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// COLUMN 4 — deliverable tiles
// =============================================================================

function DeliverablesPreview() {
  return (
    <div className="relative rounded-lg p-1" aria-hidden="true">
      <div className="grid grid-cols-3 gap-2">
        <DeliverableTile label="Doc" icon={<DocIcon className="w-6 h-6" />} />
        <DeliverableTile label="Sheet" icon={<SheetIcon className="w-6 h-6" />} />
        <DeliverableTile
          label="Notebook"
          icon={<BookOpen className="w-6 h-6" strokeWidth={1.6} />}
        />
      </div>
      <span
        className="absolute -bottom-2 -right-2 inline-flex items-center justify-center
                   w-7 h-7 rounded-full bg-success text-white shadow-bark-sm border-2 border-bg-warm"
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    </div>
  );
}

function DeliverableTile({
  label,
  icon,
}: {
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <div
      className="flex flex-col items-center gap-1 rounded-md border border-border-default
                 bg-surface px-2 py-3 shadow-bark-xs"
    >
      <span className="text-primary">{icon}</span>
      <span className="text-[10px] font-medium text-fg-secondary">{label}</span>
    </div>
  );
}

// Inline replicas of the existing DocIcon / SheetIcon (live in ArtifactDropdown.tsx
// where they are not exported). Re-stating here keeps this component self-contained.
function DocIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8 12h8v2H8v-2zm0 4h8v2H8v-2zm0-8h3v2H8V8z" />
    </svg>
  );
}

function SheetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h2v2H7zm4 0h6v2h-6zm-4 4h2v2H7zm4 0h6v2h-6zm-4 4h2v2H7zm4 0h6v2h-6z" />
    </svg>
  );
}
